import { useState, useEffect } from 'react';
import { client } from '../../sdkAdapter';
import Swal from 'sweetalert2';
import {
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
} from "../ui/table";
import { Modal } from "../ui/modal";
import Badge from "../ui/badge/Badge";

export default function CMSMenus() {
    const [menus, setMenus] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [navbarMode, setNavbarMode] = useState<'categories' | 'menus'>('menus');
    const [savingMode, setSavingMode] = useState(false);
    const [form, setForm] = useState({
        name: '',
        slug: '',
        url: '',
        sort: 0,
        active: true,
        parent_id: null as number | null
    });

    useEffect(() => {
        fetchMenus();
        fetchNavbarSetting();
    }, []);

    const fetchMenus = async () => {
        try {
            const res = await client.getFrontMenus() as any;
            const data = res.data?.data?.data || res.data?.data || res.data || [];
            setMenus(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Failed to fetch menus:', err);
            setMenus([]);
        }
    };

    const fetchNavbarSetting = async () => {
        try {
            const res = await client.getFrontNavbarMode() as any;
            const mode = res.data?.data?.navbar_mode || res.data?.navbar_mode || res?.navbar_mode;
            if (mode) setNavbarMode(mode);
        } catch (err) {
            console.error('Failed to fetch navbar setting:', err);
        }
    };

    const handleModeChange = async (newMode: 'categories' | 'menus') => {
        if (newMode === navbarMode || savingMode) return;

        const previousMode = navbarMode;
        setNavbarMode(newMode);
        setSavingMode(true);

        try {
            await client.postV1NavbarModeUpdate({
                postV1NavbarModeUpdateRequest: { navbar_mode: newMode }
            });

            Swal.fire({
                icon: 'success',
                title: 'Updated!',
                text: `Navigation set to ${newMode}`,
                timer: 1500,
                showConfirmButton: false
            });
        } catch (err) {
            console.error('Failed to update navbar mode:', err);
            setNavbarMode(previousMode);
            Swal.fire('Error', 'Could not save setting.', 'error');
        } finally {
            setSavingMode(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!form.name.trim() || !form.slug.trim() || !form.url.trim()) {
            Swal.fire({
                title: 'Validation Error',
                text: 'Name, Slug, and URL are required',
                icon: 'warning'
            });
            return;
        }
        setLoading(true);
        try {
            const payload = {
                id: editingId,
                name: form.name,
                slug: form.slug,
                url: form.url,
                sort: Boolean(form.sort),
                active: form.active,
                parent_id: form.parent_id
            };

            if (editingId) {
                await (client as any).postV1MenuUpdateId({
                    id: editingId.toString(),
                    postV1MenuUpdateIdRequest: payload
                });
            } else {
                await client.postV1MenuCreate({
                    postV1MenuCreateRequest: payload
                } as any);
            }
            setIsModalOpen(false);
            setForm({ name: '', slug: '', url: '', sort: 0, active: true, parent_id: null });
            setEditingId(null);
            fetchMenus();

            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'success',
                title: editingId ? 'Menu updated successfully' : 'Menu created successfully',
                showConfirmButton: false,
                timer: 2000,
                timerProgressBar: true
            });
        } catch (err: any) {
            console.error(err);
            Swal.fire({
                title: 'Error',
                text: err?.response?.data?.message || err?.message || 'Failed to save menu',
                icon: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        setEditingId(null);
        setForm({ name: '', slug: '', url: '', sort: 0, active: true, parent_id: null });
        setIsModalOpen(true);
    };

    const handleEdit = (menu: any) => {
        setEditingId(menu.id);
        setForm({
            name: menu.name || '',
            slug: menu.slug || '',
            url: menu.url || '',
            sort: Number(menu.sort) || 0,
            active: !!menu.active,
            parent_id: menu.parent_id || null
        });
        setIsModalOpen(true);
    };

    const handleDelete = async (id: number) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                // SDK bug: postV1MenuDelete incorrectly expects postV1FooterDeleteRequest
                try {
                    await client.postV1MenuDelete({
                        // @ts-ignore
                        postV1MenuDeleteRequest: { id },
                        // @ts-ignore
                        postV1FooterDeleteRequest: { id }
                    } as any);
                } catch (sdkError) {
                    console.warn('First delete attempt failed, trying fallback', sdkError);
                    // Fallback to sending ID directly
                    await client.postV1MenuDelete({ id } as any);
                }

                fetchMenus();

                Swal.fire({
                    title: 'Deleted!',
                    text: 'Menu deleted successfully',
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false
                });
            } catch (err) {
                console.error('Failed to delete menu', err);
                Swal.fire({
                    title: 'Error',
                    text: 'Failed to delete menu',
                    icon: 'error'
                });
            }
        }
    };



    return (
        <div className="space-y-6">
            
            {/* --- NEW TOGGLE SECTION --- */}
            <div className="p-5 rounded-xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Frontend Navigation Mode</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        Choose what is displayed on the main navigation bar of your storefront.
                    </p>
                </div>
                
                {/* Segmented Control */}
                <div className="flex bg-gray-100 dark:bg-gray-800 p-1 rounded-lg shrink-0">
                    <button
                        type="button"
                        disabled={savingMode}
                        onClick={() => handleModeChange('categories')}
                        className={`px-4 py-2 text-sm font-bold rounded-md transition-all disabled:opacity-60 disabled:cursor-not-allowed ${
                            navbarMode === 'categories' 
                                ? 'bg-white dark:bg-gray-700 shadow text-gray-900 dark:text-white' 
                                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                        }`}
                    >
                        Categories
                    </button>
                    <button
                        type="button"
                        disabled={savingMode}
                        onClick={() => handleModeChange('menus')}
                        className={`px-4 py-2 text-sm font-bold rounded-md transition-all disabled:opacity-60 disabled:cursor-not-allowed ${
                            navbarMode === 'menus' 
                                ? 'bg-white dark:bg-gray-700 shadow text-gray-900 dark:text-white' 
                                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                        }`}
                    >
                        Custom Menus
                    </button>
                </div>
            </div>

            {/* Existing Table Container */}
            <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900 overflow-hidden">
                <div className="p-4 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Menus</h3>
                    <div className="flex items-center gap-2">
                        {loading && <span className="text-sm text-gray-500">Loading...</span>}
                        <button
                            onClick={handleCreate}
                            className="px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 transition-colors text-sm"
                        >
                            Create Menu
                        </button>
                    </div>
                </div>
                
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableCell isHeader className="px-5 py-3 text-start">Name</TableCell>
                                <TableCell isHeader className="px-5 py-3 text-start">Slug</TableCell>
                                <TableCell isHeader className="px-5 py-3 text-start">Status</TableCell>
                                <TableCell isHeader className="px-5 py-3 text-end">Actions</TableCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {menus.length === 0 ? (
                                <TableRow><TableCell colSpan={4} className="px-5 py-4 text-center">No menus found.</TableCell></TableRow>
                            ) : (
                                menus.map(menu => (
                                    <TableRow key={menu.id}>
                                        <TableCell className="px-5 py-4 font-medium text-gray-900 dark:text-white">{menu.name}</TableCell>
                                        <TableCell className="px-5 py-4">{menu.slug}</TableCell>
                                        <TableCell className="px-5 py-4">
                                            <Badge color={menu.active ? 'success' : 'error'} size="sm">
                                                {menu.active ? 'Active' : 'Inactive'}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="px-4 py-3 text-end">
                                            <div className="flex justify-end gap-2">
                                                <button
                                                    onClick={() => handleEdit(menu)}
                                                    className="text-blue-600 hover:underline"
                                                    title="Edit Menu"
                                                >
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                    </svg>
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(menu.id)}
                                                    className="text-red-600 hover:underline"
                                                    title="Delete Menu"
                                                >
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>

                <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} className="max-w-xl" showCloseButton={false}>
                    <div className="flex flex-col max-h-[90vh] bg-white dark:bg-gray-900 rounded-3xl overflow-hidden shadow-2xl">
                        {/* Fixed Header */}
                        <div className="px-6 py-5 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center bg-white dark:bg-gray-900">
                            <h3 className="text-xl font-bold text-gray-800 dark:text-white">
                                {editingId ? 'Edit Menu' : 'Create Menu'}
                            </h3>
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-gray-600 dark:hover:text-white transition-colors"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Scrollable Content */}
                        <div className="flex-1 overflow-y-auto p-6 space-y-6">
                            <form id="cms-menu-form" onSubmit={handleSubmit} className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                                            Name <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={form.name}
                                            onChange={e => setForm({ ...form, name: e.target.value })}
                                            placeholder="Menu name"
                                            className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all outline-none text-sm"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                                            Slug <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={form.slug}
                                            onChange={e => setForm({ ...form, slug: e.target.value })}
                                            placeholder="menu-slug"
                                            className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all outline-none text-sm"
                                            required
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                                        URL <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={form.url}
                                        onChange={e => setForm({ ...form, url: e.target.value })}
                                        placeholder="https://example.com/page"
                                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all outline-none text-sm"
                                        required
                                    />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Sort Order</label>
                                        <input
                                            type="number"
                                            value={form.sort}
                                            onChange={e => setForm({ ...form, sort: parseInt(e.target.value) || 0 })}
                                            className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all outline-none text-sm"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Parent Menu</label>
                                        <select
                                            value={form.parent_id || ''}
                                            onChange={e => setForm({ ...form, parent_id: parseInt(e.target.value) || null })}
                                            className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all outline-none text-sm appearance-none cursor-pointer"
                                        >
                                            <option value="">None</option>
                                            {menus.filter(m => m.id !== editingId).map(m => (
                                                <option key={m.id} value={m.id}>{m.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Status</label>
                                    <select
                                        value={form.active ? 'true' : 'false'}
                                        onChange={e => setForm({ ...form, active: e.target.value === 'true' })}
                                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all outline-none text-sm appearance-none cursor-pointer"
                                    >
                                        <option value="true">Active</option>
                                        <option value="false">Inactive</option>
                                    </select>
                                </div>
                            </form>
                        </div>

                        {/* Fixed Footer */}
                        <div className="px-6 py-5 border-t border-gray-100 dark:border-gray-800 flex justify-end gap-3 bg-gray-50/50 dark:bg-gray-800/30">
                            <button
                                type="button"
                                onClick={() => setIsModalOpen(false)}
                                className="px-6 py-2.5 text-sm font-bold text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                form="cms-menu-form"
                                className="px-6 py-2.5 text-sm font-bold text-white bg-brand-500 border border-brand-500 rounded-xl hover:bg-brand-600 shadow-[0_4px_12px_rgba(59,130,246,0.3)] transition-all active:scale-95"
                            >
                                {editingId ? 'Update Menu' : 'Create Menu'}
                            </button>
                        </div>
                    </div>
                </Modal>
            </div>
        </div>
    );
}